/**
 * The account corner of the top bar, and the sign-in dialog it opens.
 *
 * ── What this file decides, which is almost nothing ──────────────────────────
 *
 * Which button exists, whether a way in is offered at all, and every sentence either
 * surface says arrive in the state, already decided by [SessionBackingViewModel]. What is
 * genuinely this file's — and is the reason it is long — is the *form*: two fields, a
 * reveal toggle, four mutually exclusive button states, a focus trap, and the handful of
 * keyboard behaviours a modal has to get right before it counts as one.
 *
 * There is no sign-in SDK anywhere in this project, and that is the whole shape of the
 * change this file records. It used to own Google Identity Services — a global on
 * `window`, loaded from `accounts.google.com`, which insisted on rendering its own button
 * and calling back when that button was pressed, and which therefore forced a division of
 * labour where `:web` owned "how a credential is obtained". A username and a password are
 * two strings out of two inputs, so that division has nothing left to divide: the state
 * machine, the validation and every failure sentence live in `:client` where a phone will
 * reuse them, and what is left here is DOM.
 *
 * ── The dialog is the toolkit's furniture with a form inside it ───────────────
 *
 * The backdrop, the card, the title, the message, the button row and the text inputs all
 * wear the toolkit's own `.dt-modal*` and `.dt-modal-input` classes — see the toolkit's
 * `ConfirmDialog.kt`, whose DOM shape this follows so that LunaPin has one kind of dialog
 * rather than two that nearly match. LunaPin's own stylesheet adds only what the toolkit
 * has no opinion about: the field labels, the reveal toggle, the banners and the full-width
 * submit.
 *
 * ── Why the four submit states are four separate nodes ───────────────────────
 *
 * Blocked, ready, busy and success are mutually exclusive, and the button area is rebuilt
 * for each rather than one element being re-attributed. The busy and success states are
 * **not buttons at all**, which is what makes "a second submit while one is in flight does
 * nothing" true in the DOM rather than only in a handler: a form with no submit button in
 * it does not submit on Enter (the HTML spec blocks implicit submission for a form with
 * more than one text field and no default button). The handler in `:client` guards it too,
 * because two guards for a request that counts against a rate limiter is the right number.
 *
 * @see SessionBackingViewModel
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import se.soderbjorn.lunapin.client.viewmodel.SessionBackingViewModel
import se.soderbjorn.lunula.web.shell.HoverMenuItem
import se.soderbjorn.lunula.web.shell.attachHoverMenu

/**
 * Renders the account corner and owns the sign-in dialog.
 *
 * @param viewModel the shared backing view model; this view's only collaborator.
 */
class SignInView(
    private val viewModel: SessionBackingViewModel,
) {
    /**
     * The element handed to `AppShellSpec.extraTopbarTrailing`, and it must be **the
     * same element every time**.
     *
     * The top bar is rebuilt wholesale on every shell rerender — a focus change, a
     * drag, a theme flip — and each rebuild re-appends whatever the spec's trailing
     * list holds. Building a fresh element per rerender would work exactly once: the
     * new element would have no click listener until it was re-created again, and the
     * symptom is a corner that stops responding after the first unrelated interaction.
     * Moving an element between parents preserves its listeners, so one stable node
     * re-appended is the whole answer.
     */
    val root: HTMLElement = element("div", "lp-account")

    /** The last state, so [startSignIn] can be called from outside a render. */
    private var lastState: SessionBackingViewModel.State? = null

    /** The dialog while it is up, or null. */
    private var dialog: SignInDialog? = null

    /**
     * Paint one state.
     *
     * Rebuilds the corner's contents wholesale rather than toggling visibility on a set
     * of pre-built nodes. There is nothing inside to preserve — no caret, no scroll, no
     * selection — and state changes here are rare (a sign-in, a sign-out), so the win is
     * that the render is a pure function of the state with no branch that can leave a
     * stale node behind. [root] itself is untouched, which is the part that has to be
     * stable.
     */
    fun render(state: SessionBackingViewModel.State) {
        // The combined flow behind this emits more than once per visible change — a
        // sign-in resolves the session and then clears the in-progress flag — and every
        // rebuild below re-attaches a hover menu. Structural equality is the whole guard
        // needed, since the state is a data class of data classes by construction.
        if (state == lastState) return
        lastState = state
        // The dialog is told first, and deliberately: a completed sign-in is the one state
        // it has something left to say about, and it says it before taking itself down.
        dialog?.render(state)
        // A deployment that has stopped offering a way in cannot be signed in to from a
        // dialog, so the dialog goes. Reachable only if the server were restarted without
        // its variables under a page that is still open.
        if (state.signedOut?.canSignIn == false) dismissDialog()

        root.innerHTML = ""

        state.signedIn?.let { signedIn ->
            root.appendChild(accountButton(signedIn))
            // A failed sign-out, in the corner of the app it failed to leave. Reported
            // here rather than swallowed because the cookie survived it: the next page
            // load signs this browser straight back in, and somebody who is about to walk
            // away from the machine has to know that.
            signedIn.errorMessage?.let { message ->
                val notice = element("div", "lp-account-error", message)
                // The sentence can be longer than the corner is wide, so the full text is
                // also the tooltip — and `role="alert"` is what makes a reader hear all of
                // it regardless of how much fits.
                notice.title = message
                notice.setAttribute("role", "alert")
                root.appendChild(notice)
            }
            return
        }
        state.signingIn?.let {
            // Still the corner's own button, disabled and renamed, rather than a
            // spinner: the shape does not move, so the corner does not jump.
            val busy = button("Signing in…", "lp-account-btn") {}
            busy.disabled = true
            root.appendChild(busy)
            return
        }
        val signedOut = state.signedOut
            // Loading: nothing at all. Flashing "Sign in…" at somebody who already has
            // a session is a worse first impression than a beat of empty space, and this
            // is the beat.
            ?: return
        // A deployment with no credentials configured offers nothing here. The landing
        // surface carries the explanation instead — a button that fails on click reads as
        // our bug, and this one could not even open a useful dialog.
        if (!signedOut.canSignIn) return
        root.appendChild(button("Sign in…", "lp-account-btn lp-account-signin") { startSignIn() })
    }

    /**
     * Open the sign-in dialog.
     *
     * Public because the corner is not the only door: the signed-out landing card
     * carries its own button (see [LandingSurface]), and it has to open the *same*
     * dialog rather than a parallel path that drifts from this one. A no-op before the
     * first session response, which is the same beat the corner renders nothing for.
     */
    fun startSignIn() {
        val signedOut = lastState?.signedOut ?: return
        if (!signedOut.canSignIn) return
        if (dialog != null) return
        val opened = SignInDialog(
            onSubmit = { username, password -> viewModel.onSignInSubmitted(username, password) },
            onEdited = { viewModel.onCredentialEdited() },
            onDismissRequested = { dismissDialog() },
        )
        dialog = opened
        opened.mount()
        lastState?.let { opened.render(it) }
    }

    // ── The corner ───────────────────────────────────────────────────────────

    /**
     * The signed-in corner: the person's name, with sign-out under it.
     *
     * The menu comes from the toolkit's [attachHoverMenu] rather than a panel of our
     * own, for two reasons that both matter. It looks like the rest of the chrome,
     * because it *is* the rest of the chrome. And it mounts its panel on `<body>`, which
     * is the only place a menu hanging off the top bar can be: the top bar is its own
     * stacking context, so a panel inside it paints *underneath* the landing surface at
     * z-index 9000 — visible, correctly sized, and behind a page.
     */
    private fun accountButton(signedIn: SessionBackingViewModel.State.SignedIn): HTMLButtonElement {
        val anchor = button(signedIn.name, "lp-account-btn lp-account-name") {}
        anchor.title = signedIn.username
        anchor.setAttribute("aria-haspopup", "menu")
        // The name appears without any surrounding text changing, so without this a
        // screen reader announces nothing at all when a sign-in lands.
        anchor.setAttribute("aria-live", "polite")
        attachHoverMenu(anchor) {
            listOf(
                HoverMenuItem(
                    id = "sign-out",
                    label = "Sign out",
                    iconHtml = SIGN_OUT_GLYPH,
                    onSelect = { viewModel.onSignOutClicked() },
                ),
            )
        }
        return anchor
    }

    // ── The dialog's lifetime ────────────────────────────────────────────────

    /**
     * Take the dialog down, if it is up. Safe to call at any time.
     *
     * The model is told, which is what clears a failure message so that the next open
     * starts clean rather than presenting the last attempt's refusal as if it were about
     * this one.
     */
    private fun dismissDialog() {
        val open = dialog ?: return
        dialog = null
        open.dispose()
        viewModel.onSignInDismissed()
    }

    private companion object {
        /** A door with an arrow leaving it — the toolkit's stroke weight and 16-unit box. */
        const val SIGN_OUT_GLYPH =
            """<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" """ +
                """stroke-linecap="round" stroke-linejoin="round">""" +
                """<path d="M6 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2H6"/>""" +
                """<path d="M10.5 11 14 8l-3.5-3"/><path d="M14 8H6"/></svg>"""
    }
}

/**
 * The username-and-password dialog.
 *
 * Built once when it opens and then only *repainted in the two places that change* — the
 * banner and the button area. The fields themselves are never rebuilt, which is not an
 * optimisation: rebuilding an input under somebody's hands loses what they typed, their
 * caret and their focus, and a failed attempt is exactly the moment they need all three.
 * That is why this is a class holding element references rather than a `render(state)`
 * that redraws a card.
 *
 * @param onSubmit called with the two field values. Everything about what happens next —
 *   the in-flight guard, the trim, the refusal to send a blank half — belongs to
 *   `:client`; this only reports the gesture.
 * @param onEdited called on every keystroke in either field, so that a showing failure
 *   stops contradicting what is now in the box.
 * @param onDismissRequested called for Cancel, Escape and a backdrop click. Routed back
 *   through [SignInView] rather than self-destructing, so that the view's idea of whether
 *   a dialog is up cannot disagree with whether one is in the document.
 */
private class SignInDialog(
    private val onSubmit: (String, String) -> Unit,
    private val onEdited: () -> Unit,
    private val onDismissRequested: () -> Unit,
) {
    private val backdrop = element("div", "dt-modal-backdrop lp-signin-backdrop")
    private val card = document.createElement("form") as HTMLFormElement
    private val usernameInput = credentialInput("text", id = "lp-signin-username")
    private val passwordInput = credentialInput("password", id = "lp-signin-password")
    private val revealToggle = button(REVEAL_SHOW, "lp-signin-reveal") { toggleReveal() }
    private val bannerSlot = element("div", "lp-signin-banner-slot")
    private val actionSlot = element("div", "lp-signin-action")

    /**
     * Whatever had focus when this opened, so it can have it back.
     *
     * Checked against the document on the way out rather than trusted: the top bar
     * rebuilds itself on any shell rerender, so the button that opened this may be a
     * detached node by the time it closes, and focusing a detached node silently moves
     * focus to `<body>` instead of leaving it where it is.
     */
    private val opener: HTMLElement? = document.activeElement as? HTMLElement

    /** Installed on `document` while this is up, and removed on the way out. */
    private var keyHandler: ((Event) -> Unit)? = null

    /** The success beat's timer, so dismissal cannot leave it firing into a dead dialog. */
    private var successTimer: Int? = null

    /** The last state painted, so the two live slots are not rebuilt for nothing. */
    private var painted: Painted? = null

    fun mount() {
        card.className = "dt-modal lp-signin"
        card.setAttribute("role", "dialog")
        card.setAttribute("aria-modal", "true")
        card.setAttribute("aria-labelledby", TITLE_ID)
        // `novalidate` because there is no constraint here a browser could check that this
        // form does not already check itself, and the native bubble it would paint instead
        // is unthemed and appears outside the card.
        card.setAttribute("novalidate", "")

        val title = element("h2", "dt-modal-title", "Sign in")
        title.id = TITLE_ID
        card.appendChild(title)
        card.appendChild(
            element(
                "p",
                "dt-modal-message lp-signin-lede",
                "Sign in to LunaPin with your username and password.",
            ),
        )

        card.appendChild(field("USERNAME", usernameInput, trailing = null))
        revealToggle.setAttribute("aria-pressed", "false")
        card.appendChild(field("PASSWORD", passwordInput, trailing = revealToggle))
        card.appendChild(bannerSlot)
        card.appendChild(actionSlot)

        // The footer rule, and only Cancel on it. The mockup's instance label was dropped:
        // on the web the server is always this origin, so it would be a line of chrome
        // saying "here".
        val footer = element("div", "dt-modal-buttons lp-signin-footer")
        footer.appendChild(button("Cancel", "dt-modal-btn dt-modal-btn-cancel") { onDismissRequested() })
        card.appendChild(footer)

        backdrop.appendChild(card)
        document.body?.appendChild(backdrop)

        // Enter from either field arrives here, because the ready state's button is a real
        // `type="submit"`. Prevented, always: a form that reached its default action would
        // navigate, and a navigation is a page load that discards the app.
        card.addEventListener("submit", { ev ->
            ev.preventDefault()
            submit()
        })
        backdrop.addEventListener("click", { ev -> if (ev.target === backdrop) onDismissRequested() })
        val handler: (Event) -> Unit = { ev -> onKeyDown(ev as KeyboardEvent) }
        document.addEventListener("keydown", handler)
        keyHandler = handler

        // Autofocus, on the field somebody is going to fill in first. Not the submit
        // button: this dialog exists to be typed into.
        usernameInput.focus()
    }

    /**
     * Repaint the banner and the button area for [state].
     *
     * Everything this reads is derived from the shared state rather than tracked here, so
     * there is no local copy of "am I busy" to fall out of step with the model that
     * actually knows.
     */
    fun render(state: SessionBackingViewModel.State) {
        val next = Painted(
            isBusy = state.signingIn != null,
            isSignedIn = state.signedIn != null,
            errorMessage = state.signedOut?.errorMessage,
        )
        if (next == painted) return
        painted = next

        bannerSlot.innerHTML = ""
        actionSlot.innerHTML = ""

        when {
            next.isSignedIn -> {
                bannerSlot.appendChild(banner("lp-signin-banner lp-signin-banner-ok", "Signed in."))
                // Not a button. The dialog is on its way out and the only honest thing to
                // offer is nothing.
                actionSlot.appendChild(element("div", "lp-signin-submit lp-signin-submit-ok", "Signed in"))
                // `main.kt` reloads the page the moment an account arrives, so this beat is
                // usually cut short by the reload — which is the intent. The timer exists
                // for the case where it is not: a dialog left saying "Signed in" over a
                // usable app is a modal nobody can get rid of.
                successTimer = window.setTimeout({ onDismissRequested() }, SUCCESS_LINGER_MS)
            }

            next.isBusy -> {
                actionSlot.appendChild(
                    element("div", "lp-signin-submit lp-signin-submit-busy").also { busy ->
                        busy.appendChild(element("span", "lp-signin-spinner"))
                        busy.appendChild(element("span", "lp-signin-submit-label", "Signing in…"))
                    },
                )
            }

            else -> {
                next.errorMessage?.let {
                    bannerSlot.appendChild(banner("lp-signin-banner lp-signin-banner-error", it))
                }
                actionSlot.appendChild(submitButton())
            }
        }
    }

    /**
     * Take this dialog out of the document and leave nothing behind.
     *
     * The password is cleared before the node is dropped. Removing the card would already
     * make it unreachable from the page, but the string would still be in the detached
     * input for as long as anything held a reference to it — and this class does, until it
     * is collected. Clearing is one line and makes the lifetime of the secret the lifetime
     * of the dialog.
     */
    fun dispose() {
        successTimer?.let { window.clearTimeout(it) }
        successTimer = null
        passwordInput.value = ""
        backdrop.parentNode?.removeChild(backdrop)
        // Removed rather than left to no-op: [SignInView] lives for the whole page, so an
        // accumulating pile of dead key handlers would be one per dialog ever opened.
        keyHandler?.let { document.removeEventListener("keydown", it) }
        keyHandler = null
        // Focus goes back where it came from, but only if that node is still in the page.
        opener?.takeIf { document.contains(it) }?.focus()
    }

    // ── Interaction ──────────────────────────────────────────────────────────

    private fun submit() {
        onSubmit(usernameInput.value, passwordInput.value)
    }

    private fun toggleReveal() {
        val revealed = passwordInput.type == "text"
        passwordInput.type = if (revealed) "password" else "text"
        revealToggle.textContent = if (revealed) REVEAL_SHOW else REVEAL_HIDE
        revealToggle.setAttribute("aria-pressed", (!revealed).toString())
        // Focus follows the value rather than the control that changed it: somebody who
        // reveals a password is checking a character they mistyped, and the next thing they
        // do is fix it.
        passwordInput.focus()
    }

    /**
     * Escape and Tab, which are the two keys a modal owns.
     *
     * **Escape only when this is the topmost dialog.** There is no shared dialog registry
     * in the toolkit — `showConfirmDialog` also listens on `document` and acts
     * unconditionally — so "topmost wins" is established here by checking that no later
     * `.dt-modal-backdrop` was appended after ours. Without that, a confirmation opened
     * over this one would take both down with a single press.
     */
    private fun onKeyDown(ev: KeyboardEvent) {
        when (ev.key) {
            "Escape" -> {
                if (!isTopmostModal()) return
                ev.preventDefault()
                onDismissRequested()
            }

            "Tab" -> trapFocus(ev)
        }
    }

    private fun isTopmostModal(): Boolean {
        val stack = document.querySelectorAll(".dt-modal-backdrop")
        return stack.length == 0 || stack.item(stack.length - 1) === backdrop
    }

    /**
     * Keep Tab inside the card.
     *
     * Only the two wrap points are handled; everything between them is the browser's own
     * tab order, which is already correct because the DOM order is the reading order.
     * Without this, tabbing past Cancel walks into the shell behind the dialog — which on
     * the signed-out surface is a tab strip that has been marked `inert`, so focus simply
     * disappears and the next Tab starts again from the address bar.
     */
    private fun trapFocus(ev: KeyboardEvent) {
        val stops = focusable()
        if (stops.isEmpty()) return
        val active = document.activeElement
        if (ev.shiftKey && active === stops.first()) {
            ev.preventDefault()
            stops.last().focus()
        } else if (!ev.shiftKey && active === stops.last()) {
            ev.preventDefault()
            stops.first().focus()
        }
    }

    /** Every enabled control in the card, in DOM order. */
    private fun focusable(): List<HTMLElement> {
        val nodes = card.querySelectorAll("input, button")
        return (0 until nodes.length)
            .mapNotNull { nodes.item(it) as? HTMLElement }
            .filter { it.asDynamic().disabled != true }
    }

    // ── Building blocks ──────────────────────────────────────────────────────

    /**
     * One labelled field: a micro-label row, then an inset input.
     *
     * @param trailing an optional control that sits on the label's row, right-aligned —
     *   the password reveal, and nothing else so far. On the label's row rather than
     *   inside the field, so it never overlaps a long value and never has to be excluded
     *   from the field's own click target.
     */
    private fun field(label: String, input: HTMLInputElement, trailing: HTMLElement?): HTMLElement {
        val wrapper = element("div", "lp-signin-field")
        val head = element("div", "lp-signin-field-head")
        val labelEl = element("label", "lp-signin-label", label)
        labelEl.setAttribute("for", input.id)
        head.appendChild(labelEl)
        trailing?.let { head.appendChild(it) }
        wrapper.appendChild(head)
        wrapper.appendChild(input)
        return wrapper
    }

    /**
     * The ready state's button, and the only `type="submit"` in the card.
     *
     * Blocked and ready are the same element with two classes rather than two branches,
     * because they are one control in two conditions — and `disabled` is what makes the
     * blocked one genuinely unpressable rather than merely styled as such. Blocked is
     * painted as *unavailable*, never as broken: a control that looks damaged reads as the
     * app being damaged.
     */
    private fun submitButton(): HTMLButtonElement {
        val blocked = usernameInput.value.isBlank() || passwordInput.value.isEmpty()
        val btn = document.createElement("button") as HTMLButtonElement
        btn.type = "submit"
        btn.textContent = "Sign in"
        btn.className = if (blocked) {
            "dt-modal-btn lp-signin-submit lp-signin-submit-blocked"
        } else {
            "dt-modal-btn dt-modal-btn-confirm lp-signin-submit lp-signin-submit-ready"
        }
        btn.disabled = blocked
        return btn
    }

    private fun banner(className: String, message: String): HTMLElement {
        val el = element("div", className, message)
        // A failure a screen reader never hears is a dialog that silently did nothing.
        el.setAttribute("role", "alert")
        return el
    }

    /**
     * One of the two credential inputs.
     *
     * ── These fields ask not to be filled in ─────────────────────────────────
     *
     * They used to do the opposite. `autocomplete="username"` and `"current-password"`
     * are the pair browsers look for, and naming them is what makes a password manager
     * offer to fill and to save — which is ordinarily the right thing to do and is not
     * what this app wants. The dropdown of saved accounts, the manager's own icon
     * inside the field and the "save this password?" bar afterwards are all declined.
     *
     * Four attributes, because there is no single switch:
     *
     *  - `autocomplete="off"` is the standard one, and the honest declaration of
     *    intent. **Browsers are entitled to ignore it on a credential pair**, and
     *    Chrome documents that it sometimes does — so this is necessary and not
     *    sufficient.
     *  - `data-1p-ignore`, `data-lpignore` and `data-form-type="other"` are the
     *    opt-outs 1Password, LastPass/Bitwarden and Dashlane each read. Extensions do
     *    not honour each other's attributes and none of them honours `autocomplete`
     *    alone, which is why there are three rather than one.
     *
     * What is deliberately *not* done to strengthen this: renaming the fields to
     * something a heuristic would not recognise, or hiding the password field behind a
     * decoy. Both work by making the form unreadable to software, and the software that
     * reads a form first is a screen reader. A field a manager might still offer to fill
     * is a smaller cost than a field nobody can navigate.
     *
     * ── The rest ─────────────────────────────────────────────────────────────
     *
     * `spellcheck` off because a username is not prose and a red underline under a
     * correct one is a suggestion that something is wrong. Autocapitalisation off
     * because the web is also a phone browser, and a keyboard that capitalises the first
     * letter of a case-sensitive username produces a refusal nobody can see the cause of.
     *
     * Wears the toolkit's `.dt-modal-input` — the same inset field its own world prompts
     * use — plus one class of ours for the size and the focus ring.
     *
     * @param id the element's own id, which the label's `for` points at. Passed rather
     *   than derived from the autocomplete value as it once was: both fields now declare
     *   the same `off`, so deriving it would give them the same id — two elements, one
     *   id, and a label that points at whichever the browser found first.
     */
    private fun credentialInput(type: String, id: String): HTMLInputElement {
        val input = document.createElement("input") as HTMLInputElement
        input.type = type
        input.id = id
        input.className = "dt-modal-input lp-signin-input"
        input.setAttribute("autocomplete", "off")
        // The three big managers, each of which reads only its own attribute.
        input.setAttribute("data-1p-ignore", "")
        input.setAttribute("data-lpignore", "true")
        input.setAttribute("data-form-type", "other")
        input.spellcheck = false
        input.setAttribute("autocapitalize", "none")
        input.addEventListener("input", {
            onEdited()
            // The blocked/ready split is a function of the two field values, and nothing
            // upstream knows them — the model is never told what has been typed, only what
            // was submitted. So this is the one piece of state this view owns, and it is
            // recomputed here rather than tracked.
            refreshSubmit()
        })
        return input
    }

    /** Re-evaluate blocked-versus-ready in place, leaving a busy or successful state alone. */
    private fun refreshSubmit() {
        val current = painted ?: return
        if (current.isBusy || current.isSignedIn) return
        actionSlot.innerHTML = ""
        actionSlot.appendChild(submitButton())
    }

    /**
     * Exactly what the two live slots are painted from.
     *
     * A shape of its own rather than the whole [SessionBackingViewModel.State], so that an
     * unrelated emission — a sign-out failure appearing in the corner — does not rebuild a
     * banner and a button that have not changed.
     */
    private data class Painted(
        val isBusy: Boolean,
        val isSignedIn: Boolean,
        val errorMessage: String?,
    )

    private companion object {
        const val TITLE_ID = "lp-signin-title"
        const val REVEAL_SHOW = "SHOW"
        const val REVEAL_HIDE = "HIDE"

        /**
         * How long "Signed in" stays up when nothing else takes the dialog down.
         *
         * Long enough to read as a confirmation rather than a flicker, short enough that
         * it is never in the way. Usually irrelevant: `main.kt` reloads the page on a new
         * account, and the reload almost always wins.
         */
        const val SUCCESS_LINGER_MS = 900
    }
}
